import { apiGet } from "./api";

export type CloudinaryFolder =
  | "ulpgl/articles"
  | "ulpgl/events"
  | "ulpgl/activities"
  | "ulpgl/faculties"
  | "ulpgl/programs"
  | "ulpgl/avatars"
  | "ulpgl/uploads";

export type CloudinaryResourceType = "image" | "raw";

/** Limite côté client (doit rester ≤ quota Cloudinary du compte). */
export const MAX_UPLOAD_BYTES = 35 * 1024 * 1024;

export function isPdfFile(file: File): boolean {
  return (
    file.type === "application/pdf" ||
    file.name.toLowerCase().endsWith(".pdf")
  );
}

/** Cloudinary gère les PDF comme ressources « image » (meilleure lecture navigateur). */
export function cloudinaryResourceTypeForFile(file: File): CloudinaryResourceType {
  return "image";
}

export function cloudinaryUploadEndpoint(file: File, cloudName: string): string {
  const type = cloudinaryResourceTypeForFile(file);
  return `https://api.cloudinary.com/v1_1/${cloudName}/${type}/upload`;
}

/** URL Cloudinary ou chemin se terminant par .pdf */
export function isPdfCloudinaryUrl(url: string): boolean {
  if (!url) return false;
  if (/\.pdf(\?|#|$)/i.test(url)) return true;
  return /\/raw\/upload\//i.test(url);
}

async function parseCloudinaryUploadError(res: Response): Promise<string> {
  try {
    const data = await res.json();
    const msg = data?.error?.message || data?.message;
    if (msg) return String(msg);
  } catch {
    /* ignore */
  }
  const text = await res.text().catch(() => "");
  return text.slice(0, 240) || `HTTP ${res.status}`;
}

export async function uploadToCloudinary(
  file: File,
  folder: CloudinaryFolder = "ulpgl/uploads"
): Promise<string> {
  const resourceType = cloudinaryResourceTypeForFile(file);

  const sig: any = await apiGet("/cloudinary/signature", { folder, resourceType });
  if (!sig.cloudName) throw new Error("Cloudinary non configuré");

  const form = new FormData();
  form.append("file", file);
  form.append("api_key", sig.apiKey);
  form.append("timestamp", String(sig.timestamp));
  form.append("signature", sig.signature);
  form.append("folder", sig.folder);

  const res = await fetch(cloudinaryUploadEndpoint(file, sig.cloudName), {
    method: "POST",
    body: form,
  });

  if (!res.ok) {
    const detail = await parseCloudinaryUploadError(res);
    if (/invalid signature/i.test(detail)) {
      throw new Error(
        "Signature Cloudinary invalide pour ce type de fichier. Réessayez ou contactez l'administrateur."
      );
    }
    if (/file size too large|max.*size|413/i.test(detail)) {
      throw new Error(
        `Fichier refusé par Cloudinary (taille max du compte). Détail : ${detail}`
      );
    }
    throw new Error(`Échec upload : ${detail}`);
  }

  const data = await res.json();
  if (!data.secure_url) throw new Error("Réponse Cloudinary invalide (URL manquante)");
  return data.secure_url as string;
}
